// Capstone v1 API client.
// Spec: docs/capstone/api-contract.md
// Wraps the 9 backend endpoints + SSE handling for /iterate and /resume.
// Reuses tokenManager from api.ts for JWT auth; dispatches PAYMENT_REQUIRED
// events on 402 so the global paywall listener routes the user to /pay.

import { tokenManager, dispatchPaywallEventIfNeeded } from './api';
import {
  IterateHandlers,
  ProjectState,
  ProjectSummary,
} from '../types/capstone';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Shape kept here (instead of in types/capstone.ts) because the list endpoint
// only returns it, never accepts it as input.
export interface ProjectListResponse {
  projects: ProjectSummary[];
}

function authHeaders(extra: Record<string, string> = {}): HeadersInit {
  const token = tokenManager.getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.ok) {
    if (res.status === 204) return null as unknown as T;
    return res.json() as Promise<T>;
  }
  // Try to parse FastAPI's {detail: ...} body to surface a useful message.
  let detail: unknown = null;
  try {
    const body = await res.json();
    detail = body?.detail;
  } catch {
    /* ignore */
  }
  const cleaned = dispatchPaywallEventIfNeeded(detail);
  const message =
    typeof detail === 'string' ? cleaned || detail :
    JSON.stringify(detail);
  throw new Error(`HTTP ${res.status}: ${message}`);
}

// Shared SSE reader for the two streaming endpoints (/iterate, /run-command).
// Reads the response body, splits on blank-line event boundaries (buffering
// across network chunks so a partial event at a chunk boundary is never lost —
// the v3.19.5 #100 fix), and dispatches each named event to the matching
// handler. All handlers are optional, so callers wire only the events they need.
async function consumeSSEStream(
  res: Response,
  handlers: IterateHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const reader = res.body?.getReader();
  if (!reader) {
    handlers.onError?.({ message: 'streaming not supported by this browser', recoverable: false });
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    let chunk;
    try {
      chunk = await reader.read();
    } catch (err: any) {
      if (signal?.aborted) return;
      handlers.onError?.({ message: `read error: ${err?.message || err}`, recoverable: false });
      return;
    }
    if (chunk.done) break;

    buffer += decoder.decode(chunk.value, { stream: true });

    // Pop completed events; keep the trailing incomplete fragment in buffer.
    const events = buffer.split('\n\n');
    buffer = events.pop() ?? '';

    for (const block of events) {
      if (!block.trim()) continue;
      let eventName = 'message';
      let dataStr = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) {
          eventName = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
          dataStr += line.slice('data:'.length).trim();
        }
      }
      if (!dataStr) continue;
      let payload: any;
      try {
        payload = JSON.parse(dataStr);
      } catch {
        // Malformed event; surface as a soft error but keep streaming.
        handlers.onError?.({
          message: `bad SSE data for ${eventName}: ${dataStr.slice(0, 80)}`,
          recoverable: true,
        });
        continue;
      }

      switch (eventName) {
        case 'iteration_started': handlers.onIterationStarted?.(payload); break;
        case 'history_extracted': handlers.onHistoryExtracted?.(payload); break;
        case 'chat_token': handlers.onChatToken?.(payload); break;
        case 'recap_token': handlers.onRecapToken?.(payload); break;
        case 'plan_update': handlers.onPlanUpdate?.(payload); break;
        case 'container_status': handlers.onContainerStatus?.(payload); break;
        case 'tool_call_start': handlers.onToolCallStart?.(payload); break;
        case 'tool_call_progress': handlers.onToolCallProgress?.(payload); break;
        case 'tool_call_result': handlers.onToolCallResult?.(payload); break;
        case 'file_tree_update': handlers.onFileTreeUpdate?.(payload); break;
        case 'status_change': handlers.onStatusChange?.(payload); break;
        case 'paywall':
          handlers.onError?.({
            message: payload?.reason === 'credits_exhausted'
              ? "You're out of credits — buy a refill to keep building."
              : 'Payment required to continue.',
            recoverable: false,
          });
          break;
        case 'iteration_done': handlers.onIterationDone?.(payload); break;
        case 'error': handlers.onError?.(payload); break;
        default:
          // Unknown event — ignore but don't crash
          break;
      }
    }
  }
}

export const capstoneApi = {
  // GET /api/capstone/projects
  async listProjects(): Promise<ProjectListResponse> {
    const res = await fetch(`${API_BASE_URL}/api/capstone/projects`, {
      method: 'GET',
      headers: authHeaders(),
    });
    return jsonOrError<ProjectListResponse>(res);
  },

  // POST /api/capstone/projects
  // pathCtx (paths step 3): attribution when the project was launched from a
  // Learning Path deep link — marks the path's project node completed.
  async createProject(
    problem_statement: string,
    pathCtx?: { pathId: string; nodeId: string } | null
  ): Promise<ProjectState> {
    const res = await fetch(`${API_BASE_URL}/api/capstone/projects`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        problem_statement,
        ...(pathCtx ? { path_id: pathCtx.pathId, node_id: pathCtx.nodeId } : {}),
      }),
    });
    return jsonOrError<ProjectState>(res);
  },

  // GET /api/capstone/projects/{id}
  async getProject(projectId: string): Promise<ProjectState> {
    const res = await fetch(
      `${API_BASE_URL}/api/capstone/projects/${encodeURIComponent(projectId)}`,
      { method: 'GET', headers: authHeaders() },
    );
    return jsonOrError<ProjectState>(res);
  },

  // DELETE /api/capstone/projects/{id}
  async deleteProject(projectId: string): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/api/capstone/projects/${encodeURIComponent(projectId)}`,
      { method: 'DELETE', headers: authHeaders() },
    );
    await jsonOrError<void>(res);
  },

  // POST /api/capstone/projects/{id}/hibernate
  async hibernate(projectId: string): Promise<ProjectState> {
    const res = await fetch(
      `${API_BASE_URL}/api/capstone/projects/${encodeURIComponent(projectId)}/hibernate`,
      { method: 'POST', headers: authHeaders() },
    );
    return jsonOrError<ProjectState>(res);
  },

  // POST /api/capstone/projects/{id}/done — returns project state + zip_url
  async markDone(projectId: string): Promise<ProjectState & { zip_url: string }> {
    const res = await fetch(
      `${API_BASE_URL}/api/capstone/projects/${encodeURIComponent(projectId)}/done`,
      { method: 'POST', headers: authHeaders() },
    );
    return jsonOrError<ProjectState & { zip_url: string }>(res);
  },

  // GET /api/capstone/projects/{id}/files?path=...
  async getFileContent(projectId: string, path: string): Promise<{ path: string; content: string; size: number }> {
    const res = await fetch(
      `${API_BASE_URL}/api/capstone/projects/${encodeURIComponent(projectId)}/files?path=${encodeURIComponent(path)}`,
      { method: 'GET', headers: authHeaders() },
    );
    return jsonOrError<{ path: string; content: string; size: number }>(res);
  },

  // POST /api/capstone/projects/{id}/iterate (SSE stream).
  // Returns when the iteration_done event arrives or the stream errors out.
  // Handlers are invoked synchronously as events arrive — keep them cheap or
  // queue heavy work elsewhere.
  async streamIterate(
    projectId: string,
    userInput: string,
    handlers: IterateHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/api/capstone/projects/${encodeURIComponent(projectId)}/iterate`,
      {
        method: 'POST',
        headers: authHeaders({ Accept: 'text/event-stream' }),
        body: JSON.stringify({ user_input: userInput }),
        signal,
      },
    );

    if (!res.ok) {
      // Map non-stream errors through the same paywall path
      let detail: unknown = null;
      try {
        const body = await res.json();
        detail = body?.detail;
      } catch {
        /* ignore */
      }
      const cleaned = dispatchPaywallEventIfNeeded(detail);
      const message =
        typeof detail === 'string' ? cleaned || detail :
        JSON.stringify(detail);
      handlers.onError?.({ message: `HTTP ${res.status}: ${message}`, recoverable: false });
      throw new Error(`HTTP ${res.status}: ${message}`);
    }

    return consumeSSEStream(res, handlers, signal);
  },

  // POST /api/capstone/projects/{id}/run-command (SSE).
  // Re-runs ONE shell command directly against the container — no LLM, no
  // credit cost. Streams the same container_status / tool_call_* events as
  // streamIterate, so the caller drives the live terminal identically.
  async streamRunCommand(
    projectId: string,
    command: string,
    handlers: Pick<
      IterateHandlers,
      'onContainerStatus' | 'onToolCallStart' | 'onToolCallProgress' | 'onToolCallResult' | 'onError'
    >,
    signal?: AbortSignal,
    timeoutSeconds = 60,
  ): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/api/capstone/projects/${encodeURIComponent(projectId)}/run-command`,
      {
        method: 'POST',
        headers: authHeaders({ Accept: 'text/event-stream' }),
        body: JSON.stringify({ command, timeout_seconds: timeoutSeconds }),
        signal,
      },
    );

    if (!res.ok) {
      let detail: unknown = null;
      try {
        const body = await res.json();
        detail = body?.detail;
      } catch {
        /* ignore */
      }
      const cleaned = dispatchPaywallEventIfNeeded(detail);
      const message =
        typeof detail === 'string' ? cleaned || detail :
        JSON.stringify(detail);
      handlers.onError?.({ message: `HTTP ${res.status}: ${message}`, recoverable: false });
      throw new Error(`HTTP ${res.status}: ${message}`);
    }

    return consumeSSEStream(res, handlers, signal);
  },

  // POST /api/capstone/projects/{id}/resume (SSE).
  // Wakes a hibernated project — no LLM, no credit cost. Provisions the
  // container, restores the workspace snapshot, and revives the dev server,
  // streaming the same container_status events as streamIterate. Called
  // automatically on project open (after a short grace period) so users
  // don't have to send a chat message just to wake their project.
  async streamResume(
    projectId: string,
    handlers: Pick<IterateHandlers, 'onContainerStatus' | 'onError'>,
    signal?: AbortSignal,
  ): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/api/capstone/projects/${encodeURIComponent(projectId)}/resume`,
      {
        method: 'POST',
        headers: authHeaders({ Accept: 'text/event-stream' }),
        signal,
      },
    );

    if (!res.ok) {
      let detail: unknown = null;
      try {
        const body = await res.json();
        detail = body?.detail;
      } catch {
        /* ignore */
      }
      const cleaned = dispatchPaywallEventIfNeeded(detail);
      const message =
        typeof detail === 'string' ? cleaned || detail :
        JSON.stringify(detail);
      handlers.onError?.({ message: `HTTP ${res.status}: ${message}`, recoverable: false });
      throw new Error(`HTTP ${res.status}: ${message}`);
    }

    return consumeSSEStream(res, handlers, signal);
  },
};

// Absolute URL for the preview pane's <iframe> src. The backend returns
// preview_url as a root-relative path (/api/capstone/projects/.../preview/...);
// the capability token lives in the path, so no auth header is needed.
export function capstonePreviewSrc(previewUrl: string): string {
  return `${API_BASE_URL}${previewUrl}`;
}

// Strip ALL fenced ``` blocks from assistant text so the chat reads as prose.
//   - ```plan       — parsed plan lives in PlanChecklist
//   - ```tool       — tool calls surface as inline activity cards
//   - ```<lang>...  — file contents live in the right-pane CodeViewer; no
//                     need to duplicate in chat
//
// Implementation: split on ``` so even-indexed segments are OUTSIDE a fence,
// odd-indexed are INSIDE. This handles both COMPLETE blocks (paired fences)
// and STREAMING incomplete blocks (no closing fence yet) — the trailing
// incomplete fence gets dropped, so we never flash raw code mid-stream.
export function stripPlanBlocks(text: string): string {
  const parts = text.split('```');
  // 0, 2, 4, ... are the "outside fence" segments. 1, 3, 5, ... are inside.
  // If text ends mid-fence (odd number of ``` total), the last "inside" part
  // is just dropped — which is exactly what we want during streaming.
  return parts.filter((_, i) => i % 2 === 0).join('').trim();
}
